import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { SharedResourceItem } from "@/modules/hub/features/HubDashboard/api/getHubDashboard/types";
import { deleteResourceShare } from "@/api/deleteResourceShare/deleteResourceShareApi";
import { Button } from "@/components/ui/button";

interface SharedResourceCardProps {
  resource: SharedResourceItem;
  onRemove?: () => void;
}

export function SharedResourceCard({
  resource,
  onRemove,
}: SharedResourceCardProps) {
  const [isRemoving, setIsRemoving] = useState(false);

  const contentType = (resource.resourceType || "document").toLowerCase();

  const formatSharerNames = () => {
    const sharers = resource.sharedBy;
    if (sharers.length === 0) return "";
    if (sharers.length === 1) {
      return `Shared by ${sharers[0].firstName} ${sharers[0].lastName}`;
    }
    if (sharers.length === 2) {
      return `Shared by ${sharers[0].firstName} and ${sharers[1].firstName}`;
    }
    return `Shared by ${sharers[0].firstName} and ${sharers.length - 1} others`;
  };

  const handleRemove = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (resource.sharedBy.length === 0) return;

    setIsRemoving(true);
    try {
      // Delete all shares for this resource
      await Promise.all(
        resource.sharedBy.map((sharer) =>
          deleteResourceShare(sharer.shareUuid),
        ),
      );
      onRemove?.();
    } catch (error) {
      console.error("Error removing share:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  const isPartnerPage =
    resource.resourceType === "Partner Page" && resource.contentUrl;

  const cardContent = (
    <>
      <div className="relative aspect-video overflow-hidden rounded-lg mb-3">
        {resource.thumbnailUrl ? (
          <img
            alt={resource.title}
            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
            src={resource.thumbnailUrl}
          />
        ) : resource.resourceType === "File" ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
            <span
              className="inline-block relative z-10"
              dangerouslySetInnerHTML={{
                __html: resource.primaryIcon ?? "",
              }}
              style={{
                filter: "brightness(0) invert(1)",
                opacity: 0.9,
              }}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700">
            <span
              className="inline-block"
              dangerouslySetInnerHTML={{
                __html: resource.primaryIcon ?? "",
              }}
            />
          </div>
        )}
      </div>

      <div className="space-y-1">
        <p className="text-xs text-gray-400 dark:text-gray-500 font-light">
          {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
        </p>
        <h3 className="text-sm font-normal text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors line-clamp-2">
          {resource.title}
        </h3>
        <p className="text-xs text-blue-500 dark:text-blue-400 font-light">
          {formatSharerNames()}
        </p>
      </div>
    </>
  );

  return (
    <div className="group relative">
      {/* Remove Button - appears on hover */}
      <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          className="h-6 w-6 p-0 bg-black/50 hover:bg-red-500 border-0 rounded-full"
          disabled={isRemoving}
          onClick={handleRemove}
          size="sm"
          title="Remove from shared with you"
          variant="ghost"
        >
          {isRemoving ? (
            <Loader2 className="h-3 w-3 animate-spin text-white" />
          ) : (
            <X className="h-3 w-3 text-white" />
          )}
        </Button>
      </div>

      {isPartnerPage ? (
        <a
          className="block"
          href={resource.contentUrl || ""}
          rel="noopener noreferrer"
          target="_blank"
        >
          {cardContent}
        </a>
      ) : (
        <Link className="block" to={`/hub/resources/${resource.slug}`}>
          {cardContent}
        </Link>
      )}
    </div>
  );
}
